<!--
VueButtonsListMultiple Component - Vertical Button List Multi-Selection Component

================================================================================
AGENT IMPLEMENTATION GUIDE
================================================================================

COMPONENT OVERVIEW:
===================
This component provides a searchable, multi-select button list interface. Users can:
- Select multiple items by clicking buttons (toggle on/off)
- Filter buttons using a search bar
- See visual feedback for selected items (checkmark icon, warning color)
- Get validation feedback (required field indicators)

FEATURES:
=========
- Multi-selection: Returns array of selected IDs
- Search functionality: Case-insensitive filtering by Name
- Validation support: Visual indicators for required/valid states
- Disabled state: Can disable entire component
- Responsive layout: Bootstrap grid with label on left
- v-model support: Full Vue 3 two-way binding
- Legacy compatibility: Supports old 'value' prop and 'input' event

================================================================================
PROPS API:
================================================================================

REQUIRED PROPS:
---------------
- itensSelect: Array<{ID: string|number, Name: string}>
  Description: Array of selectable items
  Example: [{ ID: 1, Name: "Option A" }, { ID: 2, Name: "Option B" }]

- elemId: String
  Description: Unique identifier for the button list container
  Example: "user-roles-list"

- label: String
  Description: Label text displayed on the left
  Example: "Select Roles"

OPTIONAL PROPS:
---------------
- modelValue: Array<string|number>
  Description: Selected IDs array (v-model binding)
  Default: []
  Example: [1, 3, 5]

- value: Array<string|number>
  Description: Legacy value prop (backward compatibility)
  Default: []
  Example: [1, 3, 5]

- validation: Function
  Description: Function that returns boolean (true = valid, false = invalid)
  Default: () => true
  Example: () => selectedIds.length > 0

- ngRequired: Boolean|String
  Description: Shows required field indicator (*) and validation icons
  Default: false
  Example: true or "true"

- disabled: Boolean|String
  Description: Disables all buttons and search input
  Default: false
  Example: false or "false"

- searchPlaceholder: String
  Description: Placeholder text for search input
  Default: t('Procurar') (translated "Search")
  Example: "Filter options..."

- placeholder: String
  Description: Not used (kept for API compatibility with ButtonsList)

- callback: Function
  Description: Optional function called when selection changes
  Example: () => { log('MyComponent', 'callback', 'Selection changed'); }

EMITS:
======
- update:modelValue: Array<string|number> - Emitted when selection changes (v-model)
- input: Array<string|number> - Emitted when selection changes (legacy)

================================================================================
USAGE EXAMPLES:
================================================================================

EXAMPLE 1: Basic Multi-Select with v-model
-------------------------------------------
<script setup>
import { ref } from 'vue';
import ButtonListMultiple from '@/inputs/ButtonListMultiple.vue';

const selectedRoles = ref([]);
const roles = ref([
  { ID: 1, Name: "Admin" },
  { ID: 2, Name: "Manager" },
  { ID: 3, Name: "User" },
  { ID: 4, Name: "Guest" }
]);
</script>

<template>
  <ButtonListMultiple
    v-model="selectedRoles"
    :itensSelect="roles"
    elemId="user-roles"
    label="User Roles"
    searchPlaceholder="Search roles..."
  />
</template>

EXAMPLE 2: Required Field with Validation
------------------------------------------
<script setup>
import { ref, computed } from 'vue';
import ButtonListMultiple from '@/inputs/ButtonListMultiple.vue';

const selectedCategories = ref([]);
const categories = ref([
  { ID: 'cat1', Name: "Electronics" },
  { ID: 'cat2', Name: "Clothing" },
  { ID: 'cat3', Name: "Food" }
]);

// Validation: At least one category must be selected
const isValid = computed(() => selectedCategories.value.length > 0);
</script>

<template>
  <ButtonListMultiple
    v-model="selectedCategories"
    :itensSelect="categories"
    elemId="product-categories"
    label="Categories"
    :ngRequired="true"
    :validation="() => isValid"
    searchPlaceholder="Filter categories..."
  />
</template>

EXAMPLE 3: Disabled State
--------------------------
<script setup>
import { ref } from 'vue';
import ButtonListMultiple from '@/inputs/ButtonListMultiple.vue';

const selectedItems = ref([1, 2]);
const items = ref([
  { ID: 1, Name: "Item A" },
  { ID: 2, Name: "Item B" }
]);
const isDisabled = ref(true);
</script>

<template>
  <ButtonListMultiple
    v-model="selectedItems"
    :itensSelect="items"
    elemId="disabled-list"
    label="Items"
    :disabled="isDisabled"
  />
</template>

EXAMPLE 4: With Callback Function
----------------------------------
<script setup>
import { ref } from 'vue';
import ButtonListMultiple from '@/inputs/ButtonListMultiple.vue';

const selectedIds = ref([]);
const options = ref([
  { ID: 1, Name: "Option 1" },
  { ID: 2, Name: "Option 2" }
]);

const handleSelectionChange = () => {
  log('MyComponent', 'handleSelectionChange', 'Selected IDs:', selectedIds.value);
  // Trigger other actions, API calls, etc.
};
</script>

<template>
  <ButtonListMultiple
    v-model="selectedIds"
    :itensSelect="options"
    elemId="callback-example"
    label="Options"
    :callback="handleSelectionChange"
  />
</template>

EXAMPLE 5: Integration with API (Loading Data from Backend)
------------------------------------------------------------
<script setup>
import { ref, onMounted } from 'vue';
import httpService from '@/services/httpService';
import ButtonListMultiple from '@/inputs/ButtonListMultiple.vue';

const selectedFranquias = ref([]);
const franquias = ref([]);
const isLoading = ref(true);

// Load franchises from API
onMounted(async () => {
  try {
    const response = await httpService.get('/franquias/listar');
    if (response.success && response.dados) {
      // Transform API data to component format
      franquias.value = response.dados.map(f => ({
        ID: f.franquia_id,
        Name: f.franquia_nome
      }));
    }
  } catch (error) {
    console.error('Error loading franchises:', error);
  } finally {
    isLoading.value = false;
  }
});
</script>

<template>
  <div v-if="isLoading">Loading...</div>
  <ButtonListMultiple
    v-else
    v-model="selectedFranquias"
    :itensSelect="franquias"
    elemId="franchise-selector"
    label="Franchises"
    searchPlaceholder="Search franchises..."
  />
</template>

EXAMPLE 6: In a Form with Submission
-------------------------------------
<script setup>
import { ref } from 'vue';
import httpService from '@/services/httpService';
import ButtonListMultiple from '@/inputs/ButtonListMultiple.vue';

const formData = ref({
  user_name: '',
  selected_permissions: []
});

const permissions = ref([
  { ID: 'read', Name: "Read Access" },
  { ID: 'write', Name: "Write Access" },
  { ID: 'delete', Name: "Delete Access" }
]);

const submitForm = async () => {
  try {
    const response = await httpService.post('/users/save', {
      name: formData.value.user_name,
      permissions: formData.value.selected_permissions // Array of IDs
    });

    if (response.success) {
      log('MyComponent', 'submitForm', 'User saved successfully');
    }
  } catch (error) {
    console.error('Error saving user:', error);
  }
};
</script>

<template>
  <form @submit.prevent="submitForm">
    <input v-model="formData.user_name" placeholder="User Name" />

    <ButtonListMultiple
      v-model="formData.selected_permissions"
      :itensSelect="permissions"
      elemId="user-permissions"
      label="Permissions"
      :ngRequired="true"
      :validation="() => formData.selected_permissions.length > 0"
    />

    <button type="submit">Save User</button>
  </form>
</template>

EXAMPLE 7: Pre-selecting Items (Edit Mode)
-------------------------------------------
<script setup>
import { ref, onMounted } from 'vue';
import httpService from '@/services/httpService';
import ButtonListMultiple from '@/inputs/ButtonListMultiple.vue';

const userId = ref(123);
const selectedRoles = ref([]);
const availableRoles = ref([]);

// Load user's existing roles
onMounted(async () => {
  try {
    // Load available roles
    const rolesResponse = await httpService.get('/roles/listar');
    availableRoles.value = rolesResponse.dados.map(r => ({
      ID: r.role_id,
      Name: r.role_name
    }));

    // Load user's current roles
    const userResponse = await httpService.get(`/users/get/${userId.value}`);
    if (userResponse.success && userResponse.dados.roles) {
      // Pre-select user's current roles
      selectedRoles.value = userResponse.dados.roles.map(r => r.role_id);
    }
  } catch (error) {
    console.error('Error loading data:', error);
  }
});
</script>

<template>
  <ButtonListMultiple
    v-model="selectedRoles"
    :itensSelect="availableRoles"
    elemId="edit-user-roles"
    label="User Roles"
    searchPlaceholder="Search roles..."
  />
</template>

================================================================================
DATA STRUCTURE REQUIREMENTS:
================================================================================

INPUT DATA (itensSelect):
--------------------------
Must be an array of objects with the following structure:

✅ CORRECT:
[
  { ID: 1, Name: "Option 1" },
  { ID: 2, Name: "Option 2" },
  { ID: "abc", Name: "Option 3" }  // IDs can be string or number
]

❌ WRONG:
[
  { id: 1, name: "Option 1" },      // Must use uppercase ID and Name
  { ID: 1 },                         // Missing Name property
  { Name: "Option 1" }               // Missing ID property
]

OUTPUT DATA (v-model):
----------------------
Returns an array of selected IDs (preserves original ID type):

Examples:
- [] (no selection)
- [1, 3, 5] (numeric IDs)
- ["abc", "xyz"] (string IDs)
- [1, "abc", 5] (mixed types)

================================================================================
CODEIGNITER BACKEND INTEGRATION:
================================================================================

BACKEND CONTROLLER (PHP):
--------------------------
// Controller method to list items
public function listar() {
    // Fetch data from model
    $data = $this->your_model->get_all();

    // Transform to component format
    $output = array_map(function($item) {
        return array(
            'ID' => $item['your_id_field'],
            'Name' => $item['your_name_field']
        );
    }, $data);

    $this->enviar = array(
        'success' => true,
        'dados' => $output,
        'error' => null
    );
    $this->_enviar();
}

// Controller method to save selected items
public function salvar() {
    $selected_ids = $this->input->post('selected_ids'); // Array of IDs

    // Process the selected IDs
    foreach ($selected_ids as $id) {
        // Save to database
        $this->your_model->save_selection($id);
    }

    $this->enviar = array(
        'success' => true,
        'dados' => null,
        'error' => null
    );
    $this->_enviar();
}

VUE FRONTEND (Composable):
---------------------------
// In src/composables/useYourDomain.js
export const loadItems = async () => {
    try {
        const response = await httpService.get('/your_controller/listar');
        if (response.success && response.dados) {
            return response.dados; // Already in {ID, Name} format
        }
    } catch (error) {
        console.error('Error loading items:', error);
        return [];
    }
};

export const saveSelection = async (selectedIds) => {
    try {
        const response = await httpService.post('/your_controller/salvar', {
            selected_ids: selectedIds
        });
        return response.success;
    } catch (error) {
        console.error('Error saving selection:', error);
        return false;
    }
};

================================================================================
VALIDATION PATTERNS:
================================================================================

1. REQUIRED FIELD (At least one selection):
   :validation="() => selectedIds.length > 0"

2. MINIMUM SELECTIONS:
   :validation="() => selectedIds.length >= 3"

3. MAXIMUM SELECTIONS:
   :validation="() => selectedIds.length <= 5"

4. RANGE VALIDATION:
   :validation="() => selectedIds.length >= 2 && selectedIds.length <= 10"

5. CUSTOM VALIDATION:
   const isValid = computed(() => {
     // Custom logic
     return selectedIds.value.includes(requiredId) && selectedIds.value.length > 1;
   });
   :validation="() => isValid"

================================================================================
COMMON ISSUES & TROUBLESHOOTING:
================================================================================

ISSUE 1: "Selected items not showing as selected"
SOLUTION: Ensure IDs in modelValue match IDs in itensSelect exactly (type matters!)
  - If backend returns "1" (string) but you pass 1 (number), they won't match
  - Use consistent ID types: selectedIds.value = response.dados.map(id => parseInt(id))

ISSUE 2: "Search not working"
SOLUTION: Ensure items have 'Name' property (case-sensitive, must be uppercase N)

ISSUE 3: "Validation not triggering"
SOLUTION: Ensure validation function is reactive:
  ✅ :validation="() => selectedIds.length > 0"
  ❌ :validation="isValid" (static, won't update)

ISSUE 4: "Component not updating when data changes"
SOLUTION: Ensure you're mutating the array correctly:
  ✅ selectedIds.value = [...newArray]
  ❌ selectedIds.value.push(item) (won't trigger reactivity in some cases)

ISSUE 5: "v-model not syncing"
SOLUTION: Ensure you're using ref() for the v-model binding:
  ✅ const selectedIds = ref([]);
  ❌ let selectedIds = [];

================================================================================
TESTING CHECKLIST:
================================================================================

When implementing this component, verify:
- [ ] Items load correctly from API
- [ ] Search filters items by name (case-insensitive)
- [ ] Clicking buttons toggles selection on/off
- [ ] Selected items show checkmark icon and warning color
- [ ] v-model array updates when items are selected/deselected
- [ ] Validation shows warning icon when invalid
- [ ] Validation shows success icon when valid
- [ ] Required field asterisk (*) displays when ngRequired="true"
- [ ] Disabled state prevents all interactions
- [ ] Search input is disabled when component is disabled
- [ ] "No results found" message shows when search returns empty
- [ ] Callback function fires when selection changes (if provided)
- [ ] Form submission includes correct array of IDs
- [ ] Pre-selected items display correctly in edit mode

================================================================================
COMPARISON WITH ButtonsList (Single-Select):
================================================================================

ButtonsList (Single-Select)          | ButtonListMultiple (This Component)
-------------------------------------|-------------------------------------
modelValue: String|Number            | modelValue: Array
Returns single ID                    | Returns array of IDs
Auto-selects first item              | No auto-select (starts empty)
Click to select (replaces previous)  | Click to toggle (add/remove)
No search functionality              | Has search bar with filtering
v-model="selectedId"                 | v-model="selectedIds"

USE ButtonsList WHEN:
- User should select exactly one option
- Single choice selection (radio button behavior)

USE ButtonListMultiple WHEN:
- User can select multiple options
- Checkbox-like behavior needed
- Need to filter through many options
- Selection count > 1 is valid

================================================================================
GLOBAL COMPONENT REGISTRATION:
================================================================================

To make this component globally available (if not already):

In src/main.js:
import ButtonListMultiple from './inputs/ButtonListMultiple.vue';
app.component('ButtonListMultiple', ButtonListMultiple);

Then use without importing:
<template>
  <ButtonListMultiple v-model="ids" :itensSelect="items" ... />
</template>

================================================================================
END OF DOCUMENTATION
================================================================================
-->

<template>
  <div class="button-list-multiple" :id="'container_' + elemId" :class="{ 'no-label': hideLabel }">
    <div v-if="!hideLabel" class="label-section" :class="{
      'has-warning': !hasValidation,
      'has-success': hasValidation
    }">
      <h5 class="label-text" :class="{ 'opacity50': isDisabled }">
        <i v-if="isRequired" class="pi pi-exclamation-triangle warning-icon" v-show="!hasValidation"></i>
        <i v-if="isRequired" class="pi pi-check success-icon" v-show="hasValidation"></i>
        <span v-if="isRequired">*</span>{{ label }}:
      </h5>
    </div>

    <div class="content-section">
      <div v-if="!hideSearch" class="search-container">
        <InputText
          v-model="searchQuery"
          :placeholder="searchPlaceholderText"
          :disabled="isDisabled"
          class="search-input"
        />
      </div>

      <div class="buttons-container">
        <Button
          v-for="item in filteredItems"
          :key="item.ID"
          :label="item.Name"
          :severity="(isSelected(item.ID) && !isDisabled) ? 'primary' : 'secondary'"
          :disabled="isDisabled"
          :data-testid="`${elemId}-${item.ID}`"
          @click="toggleSelection(item.ID)"
          class="toggle-button"
          text>
          <template #icon>
            <i class="pi pi-check" v-show="isSelected(item.ID)"></i>
          </template>
        </Button>
      </div>

      <div v-if="filteredItems.length === 0 && searchQuery" class="no-results">
        <p class="no-results-text">
          {{t('Nenhum resultado encontrado')}}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';

interface SelectItem {
  ID: string | number;
  Name: string;
}

const { t } = useI18n();

const props = defineProps({
  // Optional callback function triggered when selection changes
  callback: Function as any,

  // Array of objects with {ID, Name} structure for button options
  itensSelect: {
    type: Array as () => SelectItem[],
    default: () => []
  },

  // Unique identifier for the button list element
  elemId: String,

  // Placeholder (kept for API compatibility)
  placeholder: String,

  // Label text displayed to the left of the buttons
  label: String,

  // Validation function that returns boolean (true = valid, false = invalid)
  validation: {
    type: Function as any,
    default: () => true
  },

  // Indicates if the field is required (shows warning icon when invalid)
  ngRequired: {
    type: [String, Boolean],
    default: false
  },

  // Indicates if the field should be disabled
  disabled: {
    type: [String, Boolean],
    default: false
  },

  // Current selected values array (v-model support)
  modelValue: {
    type: Array as () => (string | number)[],
    default: () => []
  },

  // Legacy value prop for backward compatibility
  value: {
    type: Array as () => (string | number)[],
    default: () => []
  },

  // Placeholder for search input
  searchPlaceholder: {
    type: String,
    default: ''
  },

  // Hide the search bar
  hideSearch: {
    type: Boolean,
    default: false
  },

  // Hide the label section
  hideLabel: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits<{
  'update:modelValue': [value: (string | number)[]];
  'input': [value: (string | number)[]];
}>();

// Reactive data
const internalValue = ref<(string | number)[]>([...(props.modelValue || props.value || [])]);
const searchQuery = ref<string>('');
const isUpdatingFromProps = ref<boolean>(false);

// Computed properties
const isRequired = computed((): boolean =>
  props.ngRequired === true || props.ngRequired === 'true'
);

const isDisabled = computed((): boolean =>
  props.disabled === true || props.disabled === 'true'
);

const hasValidation = computed((): boolean =>
  (props.validation as () => boolean)()
);

const searchPlaceholderText = computed((): string => {
  return props.searchPlaceholder || t('Procurar');
});

const filteredItems = computed((): SelectItem[] => {
  if (!searchQuery.value || searchQuery.value.trim() === '') {
    return props.itensSelect;
  }

  const query = searchQuery.value.toLowerCase().trim();
  return props.itensSelect.filter((item: SelectItem) =>
    item.Name && item.Name.toLowerCase().includes(query)
  );
});

// Methods
const isSelected = (id: string | number): boolean => {
  return internalValue.value.includes(id);
};

const toggleSelection = (id: string | number): void => {
  if (isDisabled.value) return;

  const index = internalValue.value.indexOf(id);
  if (index > -1) {
    // Remove from selection
    internalValue.value = internalValue.value.filter((item: string | number) => item !== id);
  } else {
    // Add to selection
    internalValue.value = [...internalValue.value, id];
  }
};

// Watchers
watch(() => props.modelValue, (newVal: (string | number)[] | undefined) => {
  isUpdatingFromProps.value = true;
  internalValue.value = [...(newVal || [])];
  // Reset flag on next tick
  nextTick(() => {
    isUpdatingFromProps.value = false;
  });
}, { deep: true });

watch(() => props.value, (newVal: (string | number)[] | undefined) => {
  isUpdatingFromProps.value = true;
  internalValue.value = [...(newVal || [])];
  // Reset flag on next tick
  nextTick(() => {
    isUpdatingFromProps.value = false;
  });
}, { deep: true });

watch(internalValue, (newVal: (string | number)[]) => {
  // Only emit if change was from user interaction, not prop update
  if (!isUpdatingFromProps.value) {
    emit('update:modelValue', newVal);
    emit('input', newVal); // Keep for backward compatibility
    if (props.callback) {
      (props.callback as () => void)();
    }
  }
}, { deep: true });
</script>

<style scoped>
.button-list-multiple {
  display: flex;
  gap: 20px;
  margin: 15px 0;
  align-items: flex-start;
}

.label-section {
  flex: 0 0 200px;
  text-align: right;
  padding-right: 10px;
}

.label-text {
  margin-top: 5px;
  white-space: nowrap;
  font-size: 1rem;
  font-weight: 500;
  color: #495057;
}

.content-section {
  flex: 1 1 auto;
  min-width: 0;
}

.search-container {
  margin-bottom: 10px;
}

.search-input {
  width: 100%;
}

.buttons-container {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.toggle-button {
  width: 100%;
  justify-content: flex-start;
  text-align: left;
}

.toggle-button :deep(.p-button-label) {
  flex: 1;
  text-align: left;
}

/* Force icon color to match text color */
.toggle-button :deep(.p-button-icon),
.toggle-button :deep(.p-button-icon i),
.toggle-button :deep(i.pi-check) {
  color: inherit !important;
}

/* Selected buttons - white icon on primary background */
.toggle-button.p-button-primary :deep(.p-button-icon),
.toggle-button.p-button-primary :deep(.p-button-icon i),
.toggle-button.p-button-primary :deep(i.pi-check) {
  color: white !important;
}

/* Unselected buttons - inherit text color */
.toggle-button.p-button-secondary :deep(.p-button-icon),
.toggle-button.p-button-secondary :deep(.p-button-icon i),
.toggle-button.p-button-secondary :deep(i.pi-check) {
  color: inherit !important;
}

.no-results {
  text-align: center;
  margin-top: 10px;
}

.no-results-text {
  color: #6c757d;
  margin: 0;
  font-style: italic;
}

/* Validation states */
.has-warning .label-text {
  color: #dc3545;
}



.warning-icon {
  color: #ffc107;
  margin-right: 5px;
}

.success-icon {
  color: #28a745;
  margin-right: 5px;
}

.opacity50 {
  opacity: 0.5;
}

/* No label mode */
.no-label {
  gap: 0;
}

.no-label .content-section {
  flex: 1 1 100%;
}

/* Responsive design */
@media (max-width: 768px) {
  .button-list-multiple {
    flex-direction: column;
    gap: 10px;
  }
  
  .label-section {
    flex: none;
    text-align: left;
    padding-right: 0;
  }
  
  .label-text {
    margin-top: 0;
  }
}

@media (max-width: 576px) {
  .buttons-container {
    gap: 8px;
  }
  
  .toggle-button {
    font-size: 0.9rem;
  }
}
</style>
